import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  AlertCircle,
  ChevronRight,
  Monitor,
  Copy,
  Trash2,
} from 'lucide-react';
import { DateStrip } from './ScheduleStep/sections/DateStrip';
import { HoursGrid } from './ScheduleStep/sections/HoursGrid';
import { ScheduleAlerts } from './ScheduleStep/sections/ScheduleAlerts';
import { ConfirmAlertDialog } from './ScheduleStep/sections/ConfirmAlertDialog';
import {
  DisplayOrientationThumb,
  cleanScreenDisplayName,
} from './ScheduleStep/sections/DisplayOrientationThumb';
import {
  getDatesMissingSlots,
  formatChipDate,
  normalizeDateKey,
  findDayDataInSlots,
  isSlotAvailable,
} from './ScheduleStep/scheduleStepHelpers';
import {
  buildScheduleDisplayGroups,
  useScheduleSummary,
} from './ScheduleStep/scheduleSummaryContext';
import { enumerateDates } from '@/features/campaigns/utils/campaignUtils';
import { useAvailableSlotsMutation } from '@/features/campaigns/api/useCampaignMutations';
import { useManualAlertOptional } from '@/features/campaigns/components/ManualBuilder/ManualAlert';
import { getErrorMessage } from '@/shared/api/errors';
import type {
  AvailableSlotHour,
  BookingSlot,
  ManualCampaignForm,
  SelectedScreenMeta,
  SlotsByDate,
  StepThreeData,
} from '@/features/campaigns/types/campaign';

type ScreenWiseScheduleStepProps = {
  billboardIds: string[];
  campaignData: ManualCampaignForm;
  submitData: StepThreeData;
  setSubmitData: React.Dispatch<React.SetStateAction<StepThreeData>>;
  selectedMeta?: Record<string, SelectedScreenMeta> | SelectedScreenMeta[];
  slotsHoldExpired?: boolean;
  onBackToScreens?: () => void;
  error?: string;
  onClearError?: () => void;
  /** Hide page title when nested in the schedule workspace overlay. */
  embedded?: boolean;
};

export function ScreenWiseScheduleStep(props: ScreenWiseScheduleStepProps) {
  // Campaign dates list
  const allDates = useMemo(
    () => enumerateDates(props.campaignData.startDate, props.campaignData.endDate, 366),
    [props.campaignData.startDate, props.campaignData.endDate],
  );

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [missingSlotsOpen, setMissingSlotsOpen] = useState(false);
  const hoursSectionRef = useRef<HTMLDivElement>(null);
  const [activeDate, setActiveDate] = useState<string>(allDates[0] || '');

  // Derive ONLY the screens selected by the user in Step 2
  const selectedScreensList = useMemo<SelectedScreenMeta[]>(() => {
    if (props.selectedMeta && typeof props.selectedMeta === 'object') {
      const metaArray = Array.isArray(props.selectedMeta)
        ? props.selectedMeta
        : Object.values(props.selectedMeta);
      if (metaArray.length > 0) {
        return metaArray;
      }
    }

    if (props.billboardIds && props.billboardIds.length > 0) {
      return props.billboardIds.map((id, index) => ({
        id,
        screenName: `Selected Screen #${index + 1}`,
        addressLine: `Billboard ID: ${id.slice(-6)}`,
        city: props.campaignData.targetCities?.[0] || 'Selected City',
        thumbnail: '',
      }));
    }

    return [];
  }, [props.selectedMeta, props.billboardIds, props.campaignData.targetCities]);

  // Active Screen ID tab state: selects first screen by default
  const [activeScreenId, setActiveScreenId] = useState<string>(() => {
    if (props.submitData?.bookingSlots?.length) {
      const firstWithSlot = props.submitData.bookingSlots.find((s) => s.billboardId)?.billboardId;
      if (firstWithSlot && selectedScreensList.some((s) => s.id === firstWithSlot)) {
        return firstWithSlot;
      }
    }
    return selectedScreensList[0]?.id || '';
  });

  const activeScreen = useMemo(
    () => selectedScreensList.find((s) => s.id === activeScreenId) || null,
    [selectedScreensList, activeScreenId],
  );

  const targetScreenId = activeScreen?.id || '';

  // Independent per-screen slot bookings state: screenId -> BookingSlot[]
  const [screenSlots, setScreenSlots] = useState<Record<string, BookingSlot[]>>(() => {
    if (props.submitData?.bookingSlots?.length) {
      const map: Record<string, BookingSlot[]> = {};
      props.submitData.bookingSlots.forEach((slot) => {
        const sid = slot.billboardId || selectedScreensList[0]?.id || '';
        if (sid) {
          if (!map[sid]) map[sid] = [];
          map[sid].push(slot);
        }
      });
      return map;
    }
    return {};
  });

  // Per-screen sync slots & repetitions state: screenId -> boolean (keeps screen settings independent)
  const [screenSyncAllDates, setScreenSyncAllDates] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    selectedScreensList.forEach((scr) => {
      initial[scr.id] = true;
    });
    return initial;
  });

  const activeSyncRepsAllDates = targetScreenId
    ? (screenSyncAllDates[targetScreenId] ?? true)
    : true;

  // Default repetition multiplier for slots per screen: screenId -> number
  const [screenRepMultipliers, setScreenRepMultipliers] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    selectedScreensList.forEach((scr) => {
      initial[scr.id] = 1;
    });
    return initial;
  });

  const activeMultiplier = (activeScreen && screenRepMultipliers[activeScreen.id]) || 1;

  // Active screen slots
  const activeScreenSlots = useMemo<BookingSlot[]>(
    () => (targetScreenId ? screenSlots[targetScreenId] || [] : []),
    [screenSlots, targetScreenId],
  );

  // Format active label for HoursGrid
  const activeLabel = useMemo(() => {
    return formatChipDate(activeDate);
  }, [activeDate]);

  // Real available slots & repetitions cache per screen: screenId -> SlotsByDate
  const alert = useManualAlertOptional();
  const slotsMutation = useAvailableSlotsMutation({ silent: true });
  const [screenSlotsData, setScreenSlotsData] = useState<Record<string, SlotsByDate>>({});
  const [screenSlotsLoading, setScreenSlotsLoading] = useState<Record<string, boolean>>({});

  const datesKey = allDates.join(',');

  // Clear cached slots if reservation hold expired
  useEffect(() => {
    if (props.slotsHoldExpired) {
      setScreenSlotsData({});
    }
  }, [props.slotsHoldExpired]);

  // Fetch slots ONLY when a screen is selected on step 3 (fetches single screen: [targetScreenId])
  useEffect(() => {
    if (!allDates.length || !targetScreenId) {
      return;
    }

    // If slot data for this specific screen is already cached, reuse it
    if (screenSlotsData[targetScreenId]) {
      return;
    }

    const currentScreenId = targetScreenId;
    setScreenSlotsLoading((prev) => ({ ...prev, [currentScreenId]: true }));

    slotsMutation.mutate(
      {
        dates: allDates,
        billboardIds: [currentScreenId], // Fetch slots for this SINGLE screen only!
        customDuration: props.campaignData.customDuration || 30,
      },
      {
        onSuccess: (res) => {
          const data = res?.data || {};
          setScreenSlotsData((prev) => ({
            ...prev,
            [currentScreenId]: data,
          }));
        },
        onError: (error) => {
          setScreenSlotsData((prev) => ({
            ...prev,
            [currentScreenId]: {},
          }));
          alert?.error(
            'Couldn’t load slots',
            getErrorMessage(error, 'Failed to load availability for this display.'),
            { actions: [{ label: 'OK', primary: true }] },
          );
        },
        onSettled: () => {
          setScreenSlotsLoading((prev) => ({
            ...prev,
            [currentScreenId]: false,
          }));
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datesKey, targetScreenId, props.campaignData.customDuration]);

  const slotsData = useMemo<SlotsByDate>(
    () => (targetScreenId ? screenSlotsData[targetScreenId] || {} : {}),
    [screenSlotsData, targetScreenId],
  );

  const slotsLoading = Boolean(
    targetScreenId && screenSlotsLoading[targetScreenId],
  );

  // Available slot hours (24 hours) populated with real availableRepetitions from API
  const hours = useMemo<AvailableSlotHour[]>(() => {
    const dayData = slotsData[activeDate]?.availableSlots || [];
    return Array.from({ length: 24 }, (_, h) => {
      const startHour = String(h).padStart(2, '0');
      const endHour = String((h + 1) % 24).padStart(2, '0');
      const found = dayData.find((item) => item.hour === h);

      const availableReps = found?.availableRepetitions ?? 50;
      const totalReps = found?.totalRepetitions ?? 50;
      const status =
        availableReps === 0
          ? ('UNAVAILABLE' as const)
          : availableReps < 10
            ? ('LIMITED' as const)
            : ('AVAILABLE' as const);

      return {
        hour: h,
        formattedHour: found?.timeRange || `${startHour}:00 - ${endHour}:00`,
        status,
        remainingPlays: availableReps,
        availableRepetitions: availableReps,
        totalRepetitions: totalReps,
      };
    });
  }, [slotsData, activeDate]);

  const { registerApi } = useScheduleSummary();

  // Sync aggregated slots to parent submitData whenever screenSlots changes
  const syncParentSubmitData = useCallback(
    (nextScreenSlots: Record<string, BookingSlot[]>) => {
      const allAggregatedSlots: BookingSlot[] = [];
      Object.entries(nextScreenSlots).forEach(([scrId, slots]) => {
        slots.forEach((s) => {
          allAggregatedSlots.push({
            ...s,
            billboardId: scrId,
          });
        });
      });

      props.setSubmitData((prev) => ({
        ...prev,
        bookingSlots: allAggregatedSlots,
        appliedToAll: true,
      }));
    },
    [props.setSubmitData],
  );

  // Upsert `hour` on every campaign date for this screen (respecting per-date availability).
  const copyHourToAllDates = useCallback(
    (slots: BookingSlot[], hour: number, repetitions: number, screenId: string): BookingSlot[] => {
      const reps = Math.max(1, repetitions);
      const others = slots.filter((s) => s.hour !== hour);
      const copies: BookingSlot[] = [];
      for (const dKey of allDates) {
        const dayData = findDayDataInSlots(dKey, slotsData);
        if (dayData && Array.isArray(dayData.availableSlots)) {
          const avail = dayData.availableSlots.find((h) => h.hour === hour);
          if (!avail || !isSlotAvailable(avail)) continue;
          const max = Math.max(1, avail.availableRepetitions ?? reps);
          copies.push({ date: dKey, hour, repetitions: Math.min(reps, max), billboardId: screenId });
        } else {
          copies.push({ date: dKey, hour, repetitions: reps, billboardId: screenId });
        }
      }
      return [...others, ...copies];
    },
    [allDates, slotsData],
  );

  const setActiveSyncRepsAllDates = useCallback(
    (val: boolean) => {
      if (!targetScreenId) return;
      setScreenSyncAllDates((prev) => ({
        ...prev,
        [targetScreenId]: val,
      }));
      if (!val) {
        // Turning OFF: keep only the active date's slots; clear the copies.
        const normActiveOff = normalizeDateKey(activeDate);
        const kept = (screenSlots[targetScreenId] || []).filter(
          (s) => normalizeDateKey(s.date) === normActiveOff,
        );
        const offState = { ...screenSlots, [targetScreenId]: kept };
        setScreenSlots(offState);
        syncParentSubmitData(offState);
        return;
      }

      // Turning ON: copy hours already booked on the active date to all dates.
      const normActive = normalizeDateKey(activeDate);
      const current = screenSlots[targetScreenId] || [];
      const activeDaySlots = current.filter((s) => normalizeDateKey(s.date) === normActive);
      if (!activeDaySlots.length) return;
      let nextSlots = current;
      for (const slot of activeDaySlots) {
        nextSlots = copyHourToAllDates(nextSlots, slot.hour, slot.repetitions, targetScreenId);
      }
      const nextState = { ...screenSlots, [targetScreenId]: nextSlots };
      setScreenSlots(nextState);
      syncParentSubmitData(nextState);
    },
    [targetScreenId, activeDate, screenSlots, copyHourToAllDates, syncParentSubmitData],
  );

  // Check if slot is selected for active screen on active date
  const isSelectedSlot = useCallback(
    (_date: string, hour: number) => {
      const normActive = normalizeDateKey(activeDate);
      return activeScreenSlots.some(
        (s) => normalizeDateKey(s.date) === normActive && s.hour === hour,
      );
    },
    [activeScreenSlots, activeDate],
  );

  // Get booking slot object for active screen on active date
  const getSlotObject = useCallback(
    (_date: string, hour: number) => {
      const normActive = normalizeDateKey(activeDate);
      return activeScreenSlots.find(
        (s) => normalizeDateKey(s.date) === normActive && s.hour === hour,
      );
    },
    [activeScreenSlots, activeDate],
  );

  // Toggle slot for active screen
  const handleToggleSlot = useCallback(
    (_date: string, hour: number, _maxReps?: number, syncAll = activeSyncRepsAllDates) => {
      if (!targetScreenId || !activeDate) return;

      setScreenSlots((prev) => {
        const currentSlots = prev[targetScreenId] || [];
        const normActive = normalizeDateKey(activeDate);
        const existingIdx = currentSlots.findIndex(
          (s) => normalizeDateKey(s.date) === normActive && s.hour === hour,
        );

        let nextSlots: BookingSlot[];
        if (existingIdx >= 0) {
          if (syncAll) {
            nextSlots = currentSlots.filter((s) => s.hour !== hour);
          } else {
            nextSlots = currentSlots.filter((_, idx) => idx !== existingIdx);
          }
        } else {
          if (syncAll) {
            const otherSlots = currentSlots.filter((s) => s.hour !== hour);
            const addedSlots: BookingSlot[] = [];
            for (const dKey of allDates) {
              const dayData = findDayDataInSlots(dKey, slotsData);
              if (dayData && Array.isArray(dayData.availableSlots)) {
                const avail = dayData.availableSlots.find((h) => h.hour === hour);
                // Only select slot for date if available
                if (avail && isSlotAvailable(avail)) {
                  const maxAvail = avail.availableRepetitions ?? activeMultiplier;
                  addedSlots.push({
                    date: dKey,
                    hour,
                    repetitions: Math.min(activeMultiplier, Math.max(1, maxAvail)),
                    billboardId: targetScreenId,
                  });
                }
              } else {
                addedSlots.push({
                  date: dKey,
                  hour,
                  repetitions: activeMultiplier,
                  billboardId: targetScreenId,
                });
              }
            }
            nextSlots = [...otherSlots, ...addedSlots];
          } else {
            nextSlots = [
              ...currentSlots,
              {
                date: activeDate,
                hour,
                repetitions: activeMultiplier,
                billboardId: targetScreenId,
              },
            ];
          }
        }

        const nextState = {
          ...prev,
          [targetScreenId]: nextSlots,
        };
        syncParentSubmitData(nextState);
        return nextState;
      });
    },
    [targetScreenId, activeDate, activeMultiplier, allDates, slotsData, activeSyncRepsAllDates, syncParentSubmitData],
  );

  // Set repetitions for a specific slot on active screen
  const handleSetSlotReps = useCallback(
    (_date: string, hour: number, reps: number, syncAll = activeSyncRepsAllDates) => {
      if (!targetScreenId || !activeDate) return;

      setScreenSlots((prev) => {
        const currentSlots = prev[targetScreenId] || [];
        const validReps = Math.max(1, reps);

        let nextSlots: BookingSlot[];
        if (syncAll) {
          nextSlots = copyHourToAllDates(currentSlots, hour, validReps, targetScreenId);
        } else {
          const normActive = normalizeDateKey(activeDate);
          const existingIdx = currentSlots.findIndex(
            (s) => normalizeDateKey(s.date) === normActive && s.hour === hour,
          );
          if (existingIdx < 0) return prev;

          nextSlots = [...currentSlots];
          nextSlots[existingIdx] = {
            ...nextSlots[existingIdx],
            repetitions: validReps,
          };
        }

        const nextState = {
          ...prev,
          [targetScreenId]: nextSlots,
        };
        syncParentSubmitData(nextState);
        return nextState;
      });
    },
    [targetScreenId, activeDate, activeSyncRepsAllDates, copyHourToAllDates, syncParentSubmitData],
  );

  // Change default multiplier for active screen
  // const setRepsForActiveScreen = useCallback(
  //   (reps: number) => {
  //     if (!targetScreenId) return;
  //     const validReps = Math.max(1, reps);
  //     setScreenRepMultipliers((prev) => ({
  //       ...prev,
  //       [targetScreenId]: validReps,
  //     }));

  //     // Update existing slots for active screen with new multiplier
  //     setScreenSlots((prev) => {
  //       const currentSlots = prev[targetScreenId] || [];
  //       if (currentSlots.length === 0) return prev;

  //       const nextSlots = currentSlots.map((s) => ({
  //         ...s,
  //         repetitions: validReps,
  //       }));

  //       const nextState = {
  //         ...prev,
  //         [targetScreenId]: nextSlots,
  //       };
  //       syncParentSubmitData(nextState);
  //       return nextState;
  //     });
  //   },
  //   [targetScreenId, syncParentSubmitData],
  // );

  // Copy active screen's slots & repetitions to all other screens
  const handleCopyScheduleToAllScreens = useCallback(() => {
    if (!targetScreenId) return;

    setScreenSlots((prev) => {
      const activeSlots = prev[targetScreenId] || [];
      const nextState: Record<string, BookingSlot[]> = {};

      selectedScreensList.forEach((scr) => {
        nextState[scr.id] = activeSlots.map((s) => ({
          ...s,
          billboardId: scr.id,
        }));
      });

      syncParentSubmitData(nextState);
      return nextState;
    });

    setScreenRepMultipliers(() => {
      const next: Record<string, number> = {};
      selectedScreensList.forEach((scr) => {
        next[scr.id] = activeMultiplier;
      });
      return next;
    });
  }, [targetScreenId, activeMultiplier, selectedScreensList, syncParentSubmitData]);

  // Clear all screens schedule
  const handleClearAllScreens = useCallback(() => {
    setScreenSlots({});
    props.setSubmitData((prev) => ({
      ...prev,
      bookingSlots: [],
      appliedToAll: false,
    }));
  }, [props.setSubmitData]);

  // Stats helper for a specific screen
  const getScreenStats = useCallback(
    (screenId: string) => {
      const slots = screenSlots[screenId] || [];
      const totalReps = slots.reduce((acc, s) => acc + (s.repetitions || 1), 0);
      return { slotCount: slots.length, totalReps };
    },
    [screenSlots],
  );

  const missingDates = getDatesMissingSlots(
    props.submitData.bookingSlots,
    props.campaignData.startDate,
    props.campaignData.endDate,
  );

  // Register hierarchical schedule with the shared summary drawer
  useEffect(() => {
    const allSlots: BookingSlot[] = [];
    Object.entries(screenSlots).forEach(([scrId, slots]) => {
      slots.forEach((s) => allSlots.push({ ...s, billboardId: scrId }));
    });

    registerApi({
      displays: buildScheduleDisplayGroups(
        allSlots,
        selectedScreensList,
        selectedScreensList.map((s) => s.id),
      ),
      onRemoveSlot: (billboardId, date, hour) => {
        setScreenSlots((prev) => {
          const current = prev[billboardId] || [];
          const sync = screenSyncAllDates[billboardId] ?? true;
          const normTarget = normalizeDateKey(date);
          const nextState = {
            ...prev,
            [billboardId]: sync
              ? current.filter((s) => s.hour !== hour)
              : current.filter(
                  (s) =>
                    !(normalizeDateKey(s.date) === normTarget && s.hour === hour),
                ),
          };
          syncParentSubmitData(nextState);
          return nextState;
        });
      },
      onClearDisplay: (billboardId) => {
        setScreenSlots((prev) => {
          const nextState = { ...prev, [billboardId]: [] };
          syncParentSubmitData(nextState);
          return nextState;
        });
      },
      onClearAll: handleClearAllScreens,
    });

    return () => registerApi(null);
  }, [
    screenSlots,
    selectedScreensList,
    screenSyncAllDates,
    registerApi,
    syncParentSubmitData,
    handleClearAllScreens,
  ]);

  // Fallback if no screens selected
  if (selectedScreensList.length === 0) {
    return (
      <div className="camp-fade-up flex flex-col items-center justify-center rounded-xl border border-dashed border-[#E6E8EF] bg-white px-6 py-12 text-center">
        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
          <AlertCircle className="h-5 w-5" />
        </div>
        <h2 className="m-0 text-[16px] font-semibold text-[#1F2433]">
          No screens selected
        </h2>
        <p className="mt-1.5 mb-5 max-w-md text-[13px] text-[#6B7289]">
          Select screens in Step 2 to configure per-display schedules.
        </p>
        {props.onBackToScreens ? (
          <button
            type="button"
            onClick={props.onBackToScreens}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--dash-brand)] px-4 py-2 text-[13px] font-semibold text-white transition hover:brightness-105"
          >
            Go to Select Screens
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    );
  }

  const handleConfirmResetAll = () => {
    handleClearAllScreens();
    setResetConfirmOpen(false);
  };

  const configuredDisplayCount = useMemo(
    () =>
      selectedScreensList.filter((s) => getScreenStats(s.id).slotCount > 0)
        .length,
    [selectedScreensList, getScreenStats],
  );

  const screensMissingSlots = useMemo(
    () =>
      selectedScreensList.filter((s) => getScreenStats(s.id).slotCount === 0),
    [selectedScreensList, getScreenStats],
  );

  const activeDisplayName = activeScreen
    ? cleanScreenDisplayName(activeScreen.screenName)
    : '';

  // Review & pay blocked → open actionable dialog (replaces long toast)
  useEffect(() => {
    if (!props.error) return;
    if (screensMissingSlots.length === 0) {
      props.onClearError?.();
      return;
    }
    setMissingSlotsOpen(true);
    // Intentionally only react to error / missing count — onClearError identity may change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.error, screensMissingSlots.length]);

  const dismissMissingSlots = useCallback(() => {
    setMissingSlotsOpen(false);
    props.onClearError?.();
  }, [props.onClearError]);

  const jumpToFirstMissingScreen = useCallback(() => {
    const next = screensMissingSlots[0];
    if (next) {
      setActiveScreenId(next.id);
      window.setTimeout(() => {
        hoursSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 80);
    }
    dismissMissingSlots();
  }, [screensMissingSlots, dismissMissingSlots]);

  // On phone/tablet, nudge book-hours into view once so inventory isn't buried
  useEffect(() => {
    if (typeof window === 'undefined' || !hoursSectionRef.current) return;
    if (window.matchMedia('(min-width: 1024px)').matches) return;
    const id = window.setTimeout(() => {
      hoursSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 120);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional first-paint nudge only
  }, []);

  return (
    <div className="camp-fade-up flex w-full flex-col gap-2 text-left md:gap-2.5">
      {props.embedded ? null : (
        <header className="text-left">
          <h1 className="m-0 text-[16px] font-semibold tracking-tight text-[#1F2433] sm:text-[18px]">
            Schedule &amp; Slots
          </h1>
          <p className="mt-0.5 mb-0 hidden text-[12.5px] leading-snug text-[#6B7289] sm:block">
            Book one display at a time — pick a date, then tap hours. Use Copy to
            mirror this display onto the rest.
          </p>
        </header>
      )}

      {/* Compact display switcher — keeps booking UI above the fold */}
      <section
        className="rounded-xl border border-[#E6E8EF] bg-white text-left"
        aria-label="Selected displays"
      >
        <div className="flex flex-col gap-2 px-2.5 py-2 sm:flex-row sm:items-center sm:gap-2 sm:px-3 sm:pt-2.5 sm:pb-2">
          <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
            <Monitor className="h-3.5 w-3.5 shrink-0 text-[var(--dash-brand)] sm:h-4 sm:w-4" />
            <h2 className="m-0 shrink-0 text-[12.5px] font-semibold text-[#1F2433] sm:text-[13px]">
              1. Display
            </h2>
            <span className="rounded-md bg-[#F3F4F8] px-1.5 py-0.5 text-[10.5px] font-semibold tabular-nums text-[#6B7289] sm:text-[11px]">
              {configuredDisplayCount}/{selectedScreensList.length} done
            </span>
            {activeDisplayName ? (
              <span className="hidden min-w-0 truncate text-[12px] text-[#6B7289] lg:inline">
                Editing{' '}
                <span className="font-semibold text-[#1F2433]">
                  {activeDisplayName}
                </span>
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-1.5 sm:ml-auto sm:shrink-0">
            <button
              type="button"
              disabled={!activeScreen || activeScreenSlots.length === 0}
              onClick={handleCopyScheduleToAllScreens}
              title="Copy this display’s schedule to every other display"
              className="inline-flex min-h-[36px] flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#E6E8EF] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold text-[#1F2433] transition hover:bg-[#F8F9FB] disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none sm:text-[12px]"
            >
              <Copy className="h-3.5 w-3.5 shrink-0" />
              <span className="sm:hidden">Copy to all</span>
              <span className="hidden sm:inline">Copy schedule to all</span>
            </button>
            <button
              type="button"
              onClick={() => setResetConfirmOpen(true)}
              title="Reset schedules for all displays"
              className="inline-flex min-h-[36px] flex-1 items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-[11.5px] font-semibold text-rose-600 transition hover:bg-rose-50 sm:flex-none sm:text-[12px]"
            >
              <Trash2 className="h-3.5 w-3.5 shrink-0" />
              <span className="sm:hidden">Reset all</span>
              <span className="hidden sm:inline">Reset all schedules</span>
            </button>
          </div>
        </div>

        <div
          role="tablist"
          aria-label="Switch display"
          className="-mx-px flex gap-1.5 overflow-x-auto overscroll-x-contain border-t border-[#EEF0F5] px-2 py-1.5 [scrollbar-width:none] sm:py-2 [&::-webkit-scrollbar]:hidden"
        >
          {selectedScreensList.map((screen, idx) => {
            const isActive = screen.id === (activeScreen?.id || '');
            const stats = getScreenStats(screen.id);
            const isConfigured = stats.slotCount > 0;
            const orientation = String(screen.screenOrientation || '');
            const displayName = cleanScreenDisplayName(screen.screenName);
            const location = screen.city || '';
            const orientationLabel =
              orientation.toUpperCase() === 'PORTRAIT'
                ? 'Portrait'
                : orientation.toUpperCase() === 'LANDSCAPE'
                  ? 'Landscape'
                  : 'Screen';

            return (
              <button
                key={screen.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={`${displayName}, ${orientationLabel}${location ? `, ${location}` : ''}${isActive ? ', currently editing' : ''}`}
                title={screen.screenName}
                onClick={() => setActiveScreenId(screen.id)}
                className={`flex min-h-[44px] w-[132px] shrink-0 items-center gap-1.5 rounded-xl border px-1.5 py-1.5 text-left transition sm:w-[168px] sm:gap-2 sm:px-2 md:w-[180px] ${
                  isActive
                    ? 'border-[var(--dash-brand)] bg-[var(--dash-brand-soft)] shadow-[0_0_0_1px_var(--dash-brand)]'
                    : 'border-[#E6E8EF] bg-[#F8F9FB] hover:border-[#D0D4DE] hover:bg-white'
                }`}
              >
                <DisplayOrientationThumb
                  orientation={orientation}
                  active={isActive}
                  thumbnailUrl={screen.thumbnail || undefined}
                  index={idx + 1}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11.5px] font-semibold text-[#1F2433] sm:text-[12px]">
                    {displayName}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1 text-[9.5px] font-semibold sm:text-[10px]">
                    {isConfigured ? (
                      <span className="text-[var(--dash-success)]">
                        {stats.slotCount} booked
                      </span>
                    ) : (
                      <span className="text-amber-700">Not set</span>
                    )}
                    {isActive ? (
                      <span className="rounded bg-[var(--dash-brand)] px-1 py-px text-[8px] font-bold tracking-wide text-white uppercase">
                        Now
                      </span>
                    ) : null}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Actions are inline in the Display header (Copy / Reset) */}
      <ScheduleAlerts
        showExpired={Boolean(props.slotsHoldExpired) && activeScreenSlots.length === 0}
        allDatesCount={allDates.length}
        missingDatesCount={missingDates.length}
        needsAllDates={false}
      />

      <DateStrip
        dates={allDates}
        allDatesCount={allDates.length}
        showRangeNote={false}
        activeDate={activeDate}
        onSelectDate={setActiveDate}
        bookingSlots={activeScreenSlots}
      />

      <div ref={hoursSectionRef} className="scroll-mt-3">
        <HoursGrid
          activeLabel={activeLabel}
          isLoadingSlots={slotsLoading}
          activeDayCount={
            activeScreenSlots.filter(
              (s) => normalizeDateKey(s.date) === normalizeDateKey(activeDate),
            ).length
          }
          hours={hours}
          hideApplyToAll={true}
          activeDate={activeDate}
          defaultTotalReps={activeMultiplier}
          isSelected={isSelectedSlot}
          getSlot={getSlotObject}
          onToggle={handleToggleSlot}
          onSetReps={handleSetSlotReps}
          syncRepsAllDates={activeSyncRepsAllDates}
          setSyncRepsAllDates={setActiveSyncRepsAllDates}
          activeScreenName={
            activeScreen
              ? cleanScreenDisplayName(activeScreen.screenName)
              : undefined
          }
        />
      </div>

      {/* First-time coach: after booking one display, point to the next */}
      {activeScreen &&
      getScreenStats(activeScreen.id).slotCount > 0 &&
      configuredDisplayCount < selectedScreensList.length
        ? (() => {
            const next = selectedScreensList.find(
              (s) =>
                s.id !== activeScreen.id &&
                getScreenStats(s.id).slotCount === 0,
            );
            if (!next) return null;
            const nextName = cleanScreenDisplayName(next.screenName);
            return (
              <div className="flex flex-col gap-2 rounded-xl border border-emerald-200 bg-emerald-50/80 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-3.5 sm:py-3">
                <div className="min-w-0">
                  <p className="m-0 text-[12.5px] font-semibold text-emerald-900 sm:text-[13px]">
                    {activeDisplayName} is booked
                  </p>
                  <p className="m-0 mt-0.5 text-[11.5px] text-emerald-800/80 sm:text-[12px]">
                    {selectedScreensList.length - configuredDisplayCount} display
                    {selectedScreensList.length - configuredDisplayCount === 1
                      ? ''
                      : 's'}{' '}
                    still need hours. Next: {nextName}.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveScreenId(next.id)}
                  className="inline-flex min-h-[40px] w-full shrink-0 items-center justify-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-2 text-[12px] font-semibold text-white transition hover:bg-emerald-800 sm:w-auto"
                >
                  <span className="max-w-[70vw] truncate">Book {nextName}</span>
                  <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                </button>
              </div>
            );
          })()
        : null}

      <ConfirmAlertDialog
        open={resetConfirmOpen}
        title="Reset all schedules?"
        description={`This clears every booked hour across all ${selectedScreensList.length} selected display${selectedScreensList.length === 1 ? '' : 's'}. This cannot be undone.`}
        confirmLabel="Reset schedules"
        cancelLabel="Keep schedules"
        tone="danger"
        onCancel={() => setResetConfirmOpen(false)}
        onConfirm={handleConfirmResetAll}
      />

      <ConfirmAlertDialog
        open={missingSlotsOpen && screensMissingSlots.length > 0}
        title={
          screensMissingSlots.length === 1
            ? 'One display still needs hours'
            : `${screensMissingSlots.length} displays still need hours`
        }
        description="Every selected display needs at least one booked hour before you can continue. Jump to the next one, book hours, then try Review & pay again."
        items={screensMissingSlots.map((s) =>
          cleanScreenDisplayName(s.screenName),
        )}
        confirmLabel="Book next display"
        cancelLabel="Got it"
        tone="warning"
        onCancel={dismissMissingSlots}
        onConfirm={jumpToFirstMissingScreen}
      />
    </div>
  );
}
